import React from 'react';
import { Users, DollarSign, Wallet, CreditCard } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';

const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
};

export const DashboardTab = ({ stats, statsPeriod, setStatsPeriod, customDates, setCustomDates, bookings, setActiveTab }) => {
  const recentBookings = stats.recent_bookings || bookings.slice(0, 10);
  return (
    <div data-testid="dashboard-content">
      <div className="flex flex-wrap gap-4 mb-6 items-end">
        <div>
          <Label className="text-sm text-[#8E9A95]">Period</Label>
          <Select value={statsPeriod} onValueChange={setStatsPeriod}>
            <SelectTrigger className="w-36 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="monthly">This Month</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {statsPeriod === 'custom' && (
          <>
            <div>
              <Label className="text-sm text-[#8E9A95]">From</Label>
              <Input type="date" value={customDates.start} onChange={(e) => setCustomDates(prev => ({ ...prev, start: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label className="text-sm text-[#8E9A95]">To</Label>
              <Input type="date" value={customDates.end} onChange={(e) => setCustomDates(prev => ({ ...prev, end: e.target.value }))} className="mt-1" />
            </div>
          </>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-[#121816] p-6 rounded-lg border border-[#D4AF37]/30">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center"><Users size={24} className="text-[#D4AF37]" /></div>
            <div><p className="text-sm text-[#8E9A95]">Total Bookings</p><p className="text-2xl font-semibold text-[#E8E3D9]">{stats.total_bookings}</p></div>
          </div>
        </div>
        <div className="bg-[#121816] p-6 rounded-lg border border-[#D4AF37]/30">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center"><DollarSign size={24} className="text-blue-600" /></div>
            <div><p className="text-sm text-[#8E9A95]">Total Booking Amount</p><p className="text-2xl font-semibold text-[#E8E3D9]">₹{stats.total_booking_amount?.toLocaleString() || 0}</p></div>
          </div>
        </div>
        <div className="bg-[#121816] p-6 rounded-lg border border-[#D4AF37]/30">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-900/30 rounded-full flex items-center justify-center"><Wallet size={24} className="text-green-600" /></div>
            <div><p className="text-sm text-[#8E9A95]">Total Collection</p><p className="text-2xl font-semibold text-green-600">₹{stats.total_collection?.toLocaleString() || 0}</p></div>
          </div>
        </div>
        <div className="bg-[#121816] p-6 rounded-lg border border-[#D4AF37]/30">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-full flex items-center justify-center"><CreditCard size={24} className="text-[#D4AF37]" /></div>
            <div><p className="text-sm text-[#8E9A95]">Balance</p><p className="text-2xl font-semibold text-[#D4AF37]">₹{stats.total_balance?.toLocaleString() || 0}</p></div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <div className="bg-[#D4AF37]/10 p-4 rounded-lg border border-[#D4AF37]/30"><p className="text-sm text-[#D4AF37]">Pending</p><p className="text-xl font-semibold text-[#E5C766]">{stats.pending}</p></div>
        <div className="bg-emerald-900/20 p-4 rounded-lg border border-emerald-700/40"><p className="text-sm text-emerald-300">Confirmed</p><p className="text-xl font-semibold text-emerald-400">{stats.confirmed}</p></div>
        <div className="bg-yellow-900/20 p-4 rounded-lg border border-yellow-700/40"><p className="text-sm text-yellow-300">On Hold</p><p className="text-xl font-semibold text-yellow-400">{stats.on_hold || 0}</p></div>
        <div className="bg-red-900/20 p-4 rounded-lg border border-red-900/40"><p className="text-sm text-red-300">Cancelled</p><p className="text-xl font-semibold text-red-400">{stats.cancelled}</p></div>
      </div>
      <div className="bg-[#121816] rounded-lg border border-[#D4AF37]/30">
        <div className="p-4 border-b border-[#D4AF37]/30 flex justify-between items-center">
          <h3 className="font-semibold text-[#E8E3D9]">Recent Bookings</h3>
          <Button variant="outline" size="sm" onClick={() => setActiveTab('bookings')}>View All</Button>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Guest</TableHead><TableHead>Rooms</TableHead><TableHead>Check-in</TableHead><TableHead>Amount</TableHead><TableHead>Advance</TableHead><TableHead>Check-in Coll.</TableHead><TableHead>Balance</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {recentBookings.slice(0, 10).map(booking => {
                const roomsDisplay = (booking.room_names && booking.room_names.length > 0) ? booking.room_names.join(', ') : (booking.room_name || booking.room_type);
                return (
                  <TableRow key={booking.id}>
                    <TableCell className="font-medium">{booking.guest_name}</TableCell>
                    <TableCell className="capitalize">{roomsDisplay}</TableCell>
                    <TableCell>{fmtDate(booking.check_in_date)}</TableCell>
                    <TableCell>₹{(booking.total_price || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-green-600">₹{(booking.advance_amount || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-blue-600">₹{((booking.checkin_collection || 0) + (booking.checkin_collection_2 || 0)).toLocaleString()}</TableCell>
                    <TableCell className="text-[#D4AF37] font-medium">₹{(booking.balance || 0).toLocaleString()}</TableCell>
                    <TableCell><span className={`status-badge status-${booking.status}`}>{booking.status?.replace('_', ' ')}</span></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
